import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  downloadTransactions,
  useTransactions,
  type ExportFormat,
  type TransactionListItem,
} from "@/api/transactions";
import { formatMsk } from "@/lib/datetime";
import { useT } from "@/lib/i18n";

const PAGE = 25;

const TYPE_BADGE: Record<string, string> = {
  purchase: "bg-slate-100 text-slate-700",
  refund: "bg-amber-100 text-amber-800",
  topup: "bg-emerald-100 text-emerald-800",
  deposit: "bg-emerald-100 text-emerald-800",
  withdraw: "bg-red-100 text-red-700",
  admin_topup: "bg-emerald-100 text-emerald-800",
  admin_debit: "bg-red-100 text-red-700",
};

const TYPE_KEY: Record<string, string> = {
  purchase: "tx.type.purchase",
  refund: "tx.type.refund",
  topup: "tx.type.topup",
  deposit: "tx.type.deposit",
  withdraw: "tx.type.withdraw",
  admin_topup: "tx.type.admin_topup",
  admin_debit: "tx.type.admin_debit",
};

function isAdminTx(type: string | null | undefined): boolean {
  return type === "admin_topup" || type === "admin_debit";
}

function isCreditTx(type: string | null | undefined): boolean {
  return (
    type === "refund" ||
    type === "topup" ||
    type === "deposit" ||
    type === "admin_topup"
  );
}

function reasonFromReference(reference: unknown): string | null {
  if (reference && typeof reference === "object" && !Array.isArray(reference)) {
    const r = (reference as Record<string, unknown>).reason;
    if (typeof r === "string" && r.trim()) return r;
  }
  return null;
}

function formatReference(reference: unknown): string {
  if (reference === null || reference === undefined) return "-";
  if (typeof reference === "string") return reference;
  if (typeof reference === "object") {
    try {
      return JSON.stringify(reference);
    } catch {
      return "[object]";
    }
  }
  return String(reference);
}

function formatAmount(type: string | null, amount: number): string {
  const sign = isCreditTx(type) ? "+" : "−";
  return `${sign}${Math.abs(amount).toFixed(4)}`;
}

function amountClass(type: string | null): string {
  if (isCreditTx(type)) return "text-emerald-700";
  return "text-slate-800";
}

export function Transactions() {
  const navigate = useNavigate();
  const t = useT();
  const [page, setPage] = useState(0);
  const [type, setType] = useState<string>("");
  const [customId, setCustomId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [detailRow, setDetailRow] = useState<TransactionListItem | null>(null);
  const [exportBusy, setExportBusy] = useState<ExportFormat | null>(null);
  const [exportError, setExportError] = useState(false);

  const onExport = async (format: ExportFormat) => {
    if (exportBusy) return;
    setExportBusy(format);
    setExportError(false);
    try {
      await downloadTransactions(
        {
          type: type || undefined,
          custom_id: customId.trim() || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        },
        format,
      );
    } catch {
      setExportError(true);
    } finally {
      setExportBusy(null);
    }
  };

  const onActivateRow = (row: TransactionListItem) => {
    if (isAdminTx(row.type)) {
      setDetailRow(row);
      return;
    }
    if (row.custom_id) {
      navigate(`/orders/${encodeURIComponent(row.custom_id)}`);
    }
  };

  const offset = page * PAGE;
  const tx = useTransactions({
    offset,
    limit: PAGE,
    type: type || undefined,
    custom_id: customId.trim() || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });

  const totalPages = tx.data
    ? Math.max(1, Math.ceil(tx.data.total / PAGE))
    : 1;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">{t("tx.title")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("tx.subtitle")}</p>
      </div>

      <div className="card flex flex-wrap items-end gap-3 p-4">
        <div>
          <label className="label">{t("tx.filter.type")}</label>
          <select
            className="input"
            value={type}
            onChange={(e) => {
              setPage(0);
              setType(e.target.value);
            }}
          >
            <option value="">{t("tx.type.all")}</option>
            <option value="purchase">{t("tx.type.purchase")}</option>
            <option value="refund">{t("tx.type.refund")}</option>
            <option value="topup">{t("tx.type.topup")}</option>
            <option value="deposit">{t("tx.type.deposit")}</option>
            <option value="withdraw">{t("tx.type.withdraw")}</option>
          </select>
        </div>
        <div className="flex-1 min-w-[14rem]">
          <label className="label">{t("tx.filter.custom_id")}</label>
          <input
            type="text"
            className="input"
            placeholder={t("tx.filter.custom_id_placeholder")}
            value={customId}
            onChange={(e) => {
              setPage(0);
              setCustomId(e.target.value);
            }}
          />
        </div>
        <div>
          <label className="label">{t("tx.filter.from")}</label>
          <input
            type="date"
            className="input"
            value={dateFrom}
            onChange={(e) => {
              setPage(0);
              setDateFrom(e.target.value);
            }}
          />
        </div>
        <div>
          <label className="label">{t("tx.filter.to")}</label>
          <input
            type="date"
            className="input"
            value={dateTo}
            onChange={(e) => {
              setPage(0);
              setDateTo(e.target.value);
            }}
          />
        </div>
        <div className="ml-auto flex items-end gap-2">
          <span className="label hidden sm:block">{t("tx.export")}</span>
          <button
            type="button"
            className="btn btn-ghost text-xs"
            disabled={exportBusy !== null}
            onClick={() => onExport("csv")}
          >
            {exportBusy === "csv" ? t("tx.export.busy") : t("tx.export.csv")}
          </button>
          <button
            type="button"
            className="btn btn-ghost text-xs"
            disabled={exportBusy !== null}
            onClick={() => onExport("xlsx")}
          >
            {exportBusy === "xlsx" ? t("tx.export.busy") : t("tx.export.xlsx")}
          </button>
        </div>
      </div>

      {exportError && (
        <div className="text-sm text-red-600">{t("tx.export.error")}</div>
      )}

      {/* --- Mobile (<sm): card list --- */}
      <div className="space-y-2 sm:hidden">
        {tx.isLoading && (
          <div className="card px-4 py-6 text-center text-sm text-slate-400">
            {t("common.loading")}
          </div>
        )}
        {tx.data?.items.map((row) => (
          <TxCard
            key={row.id}
            row={row}
            onActivate={() => onActivateRow(row)}
          />
        ))}
        {tx.data && tx.data.items.length === 0 && (
          <div className="card px-4 py-6 text-center text-sm text-slate-400">
            {t("tx.empty")}
          </div>
        )}
      </div>

      {/* --- ≥sm: table --- */}
      <div className="card hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-surface-muted text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-3 font-semibold sm:px-4">{t("tx.col.date")}</th>
              <th className="px-3 py-3 font-semibold sm:px-4">{t("tx.col.type")}</th>
              <th className="px-3 py-3 text-right font-semibold sm:px-4">{t("tx.col.amount")}</th>
              <th className="px-3 py-3 text-right font-semibold sm:px-4">
                {t("tx.col.balance")}
              </th>
              <th className="hidden px-3 py-3 font-semibold md:table-cell md:px-4">
                {t("tx.col.custom_id")}
              </th>
              <th className="hidden px-3 py-3 font-semibold lg:table-cell lg:px-4">
                {t("tx.col.reference")}
              </th>
            </tr>
          </thead>
          <tbody>
            {tx.isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-slate-400">
                  {t("common.loading")}
                </td>
              </tr>
            )}
            {tx.data?.items.map((row: TransactionListItem) => (
              <tr
                key={row.id}
                onClick={() => onActivateRow(row)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onActivateRow(row);
                  }
                }}
                role="link"
                tabIndex={0}
                className="cursor-pointer border-t border-surface-border hover:bg-surface-muted focus:bg-surface-muted focus:outline-none"
              >
                <td className="px-3 py-3 text-slate-600 sm:px-4">
                  {formatMsk(row.date)}
                </td>
                <td className="px-3 py-3 sm:px-4">
                  <span
                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                      TYPE_BADGE[row.type ?? ""] ??
                      "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {TYPE_KEY[row.type ?? ""] ? t(TYPE_KEY[row.type ?? ""]) : (row.type ?? "-")}
                  </span>
                </td>
                <td
                  className={`px-3 py-3 text-right font-medium sm:px-4 ${amountClass(row.type)}`}
                >
                  {formatAmount(row.type, row.amount)}
                </td>
                <td className="px-3 py-3 text-right text-slate-500 sm:px-4">
                  {row.b_after !== null && row.b_after !== undefined
                    ? row.b_after.toFixed(4)
                    : "-"}
                </td>
                <td className="hidden px-3 py-3 font-mono text-xs text-slate-500 md:table-cell md:px-4">
                  {!row.custom_id ? (
                    "-"
                  ) : isAdminTx(row.type) ? (
                    <button
                      type="button"
                      onClick={() => setDetailRow(row)}
                      className="text-accent hover:underline"
                    >
                      {t("tx.col.details")}
                    </button>
                  ) : (
                    <Link
                      to={`/orders/${encodeURIComponent(row.custom_id)}`}
                      className="text-accent hover:underline"
                    >
                      {row.custom_id}
                    </Link>
                  )}
                </td>
                <td className="hidden max-w-[24rem] truncate px-3 py-3 text-xs text-slate-500 lg:table-cell lg:px-4">
                  <span title={formatReference(row.reference)}>
                    {isAdminTx(row.type)
                      ? reasonFromReference(row.reference) ?? "-"
                      : formatReference(row.reference)}
                  </span>
                </td>
              </tr>
            ))}
            {tx.data && tx.data.items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-slate-400">
                  {t("tx.empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {tx.data && totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-slate-500">
            {t("orders.page")} {page + 1} {t("orders.page.of")} {totalPages}
            {" · "}
            {tx.data.total} {t("orders.page.total")}
          </div>
          <div className="flex gap-2">
            <button
              className="btn btn-ghost text-xs"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              {t("orders.page.prev")}
            </button>
            <button
              className="btn btn-ghost text-xs"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              {t("orders.page.next")}
            </button>
          </div>
        </div>
      )}

      {detailRow && (
        <TxDetailModal row={detailRow} onClose={() => setDetailRow(null)} />
      )}
    </div>
  );
}

// ---- mobile card -------------------------------------------------------

function TxCard({
  row,
  onActivate,
}: {
  row: TransactionListItem;
  onActivate: () => void;
}) {
  const t = useT();
  const admin = isAdminTx(row.type);
  const reference = admin
    ? reasonFromReference(row.reference) ?? ""
    : formatReference(row.reference);
  const referenceShown = reference && reference !== "-";
  const typeLabel = TYPE_KEY[row.type ?? ""]
    ? t(TYPE_KEY[row.type ?? ""])
    : (row.type ?? "-");
  return (
    <div
      onClick={onActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onActivate();
        }
      }}
      role="link"
      tabIndex={0}
      className="card cursor-pointer space-y-2 p-3 hover:bg-surface-muted focus:outline-none"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <span
            className={`inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-medium ${
              TYPE_BADGE[row.type ?? ""] ?? "bg-slate-100 text-slate-700"
            }`}
          >
            {typeLabel}
          </span>
          <span className="text-xs text-slate-500">
            {formatMsk(row.date)}
          </span>
        </div>
        <div className="text-right">
          <div className={`text-base font-semibold ${amountClass(row.type)}`}>
            {formatAmount(row.type, row.amount)}
          </div>
          {row.b_after !== null && row.b_after !== undefined && (
            <div className="text-[11px] text-slate-400">
              bal {row.b_after.toFixed(4)}
            </div>
          )}
        </div>
      </div>
      {!admin && row.custom_id && (
        <div className="block truncate font-mono text-[11px] text-accent">
          {row.custom_id}
        </div>
      )}
      {admin && (
        <div className="block text-[11px] text-accent">{t("tx.col.details")}</div>
      )}
      {referenceShown && (
        <div className="truncate text-xs text-slate-500" title={reference}>
          {reference}
        </div>
      )}
    </div>
  );
}

// ---- admin-tx details modal --------------------------------------------

function TxDetailModal({
  row,
  onClose,
}: {
  row: TransactionListItem;
  onClose: () => void;
}) {
  const t = useT();
  const reason = reasonFromReference(row.reference);
  const typeLabel = TYPE_KEY[row.type ?? ""]
    ? t(TYPE_KEY[row.type ?? ""])
    : (row.type ?? "-");
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 60,
        background: "rgba(15, 23, 42, 0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--color-surface, white)",
          color: "var(--color-text, #111)",
          borderRadius: 14,
          width: "100%", maxWidth: 440,
          padding: 20,
          boxShadow: "0 30px 80px -20px rgba(15, 23, 42, 0.4)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>{typeLabel}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent", border: 0, fontSize: 20,
              lineHeight: 1, cursor: "pointer", color: "var(--color-text-muted, #888)",
            }}
          >
            ×
          </button>
        </div>
        <DetailRow label={t("tx.col.date")}>
          {formatMsk(row.date)}
        </DetailRow>
        <DetailRow label={t("tx.col.amount")}>
          <span className={amountClass(row.type)}>
            {formatAmount(row.type, row.amount)}
          </span>
        </DetailRow>
        {row.b_before !== null && row.b_before !== undefined && (
          <DetailRow label={t("tx.detail.balance_before")}>
            {row.b_before.toFixed(4)}
          </DetailRow>
        )}
        {row.b_after !== null && row.b_after !== undefined && (
          <DetailRow label={t("tx.detail.balance_after")}>
            {row.b_after.toFixed(4)}
          </DetailRow>
        )}
        <DetailRow label={t("tx.detail.reason")}>
          {reason ?? <span style={{ color: "var(--color-text-muted, #888)" }}>-</span>}
        </DetailRow>
      </div>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "baseline",
      gap: 12, padding: "8px 0", borderBottom: "1px solid var(--color-surface-border, #e5e7eb)",
    }}>
      <span style={{ fontSize: 12, color: "var(--color-text-muted, #888)", fontWeight: 500 }}>
        {label}
      </span>
      <span style={{ fontSize: 14, textAlign: "right", maxWidth: "70%", wordBreak: "break-word" }}>
        {children}
      </span>
    </div>
  );
}
